// qs-useColorInputs.ts
import { cmykToRgb, hexToRgb, rgbToCmyk, rgbToHex, rgbToHsl } from '@/utils/qs-util-colorUtils'
import { computed, ref, type Ref } from 'vue'

export function useColorInputs(
  red: Ref<number>,
  green: Ref<number>,
  blue: Ref<number>,
  opacity: Ref<number>,
  hue: Ref<number>,
  saturation: Ref<number>,
  lightness: Ref<number>,
  emit: (event: 'update:modelValue' | 'update:opacity', value: string | number) => void
) {
  const hexInput = ref('')
  const rInput = ref(0)
  const gInput = ref(0)
  const bInput = ref(0)
  const aInput = ref(100)
  const cInput = ref(0)
  const mInput = ref(0)
  const yInput = ref(0)
  const kInput = ref(0)

  const hexColor = computed(() => rgbToHex(red.value, green.value, blue.value))
  const cmykValues = computed(() => rgbToCmyk(red.value, green.value, blue.value))

  const validateRgbValue = (value: number): number => Math.max(0, Math.min(255, value))
  const validateOpacityValue = (value: number): number => Math.max(0, Math.min(100, value))
  const validateCmykValue = (value: number): number => Math.max(0, Math.min(100, value))
  const isValidHex = (hex: string): boolean => /^[0-9A-Fa-f]{6}$/.test(hex)

  const updateHslFromRgb = (): void => {
    const hsl = rgbToHsl(red.value, green.value, blue.value)
    hue.value = hsl.h
    saturation.value = hsl.s
    lightness.value = hsl.l
  }

  const updateInputFields = (): void => {
    hexInput.value = hexColor.value.slice(1)
    rInput.value = red.value
    gInput.value = green.value
    bInput.value = blue.value
    aInput.value = Math.round(opacity.value)
    const cmyk = cmykValues.value
    cInput.value = cmyk.c
    mInput.value = cmyk.m
    yInput.value = cmyk.y
    kInput.value = cmyk.k
  }

  const updateFromHex = (): void => {
    let hex = hexInput.value.replace('#', '')
    hexInput.value = hex

    if (isValidHex(hex)) {
      const rgb = hexToRgb('#' + hex)
      red.value = rgb.r
      green.value = rgb.g
      blue.value = rgb.b
      updateHslFromRgb()
      updateInputFields()
      emit('update:modelValue', '#' + hex)
    }
  }

  const updateFromRgba = (component: 'r' | 'g' | 'b' | 'a'): void => {
    let value = 0

    switch (component) {
      case 'r':
        value = validateRgbValue(Number(rInput.value) || 0)
        red.value = value
        rInput.value = value
        break
      case 'g':
        value = validateRgbValue(Number(gInput.value) || 0)
        green.value = value
        gInput.value = value
        break
      case 'b':
        value = validateRgbValue(Number(bInput.value) || 0)
        blue.value = value
        bInput.value = value
        break
      case 'a':
        value = validateOpacityValue(Number(aInput.value) || 0)
        opacity.value = value
        aInput.value = value
        emit('update:opacity', opacity.value)
        break
    }

    if (component !== 'a') {
      updateHslFromRgb()
      updateInputFields()
    }
    emit('update:modelValue', hexColor.value)
  }

  const updateFromCmyk = (component: 'c' | 'm' | 'y' | 'k'): void => {
    let value = 0

    switch (component) {
      case 'c':
        value = validateCmykValue(Number(cInput.value) || 0)
        cInput.value = value
        break
      case 'm':
        value = validateCmykValue(Number(mInput.value) || 0)
        mInput.value = value
        break
      case 'y':
        value = validateCmykValue(Number(yInput.value) || 0)
        yInput.value = value
        break
      case 'k':
        value = validateCmykValue(Number(kInput.value) || 0)
        kInput.value = value
        break
    }

    const rgb = cmykToRgb(cInput.value, mInput.value, yInput.value, kInput.value)
    red.value = rgb.r
    green.value = rgb.g
    blue.value = rgb.b
    updateHslFromRgb()
    updateInputFields()
    emit('update:modelValue', hexColor.value)
  }

  return {
    hexInput,
    rInput,
    gInput,
    bInput,
    aInput,
    cInput,
    mInput,
    yInput,
    kInput,
    hexColor,
    cmykValues,
    updateFromHex,
    updateFromRgba,
    updateFromCmyk,
    updateHslFromRgb,
    updateInputFields
  }
}